import { useEffect, useMemo, useState } from 'react'

import { loadTransactionObject } from '../../../api/designers'
import type { FormDestination, FormRecordTarget } from '@twasta/page-schema'
import { usePageRuntime } from './actions/runtime'
import { DraftTextField } from './appBlockEditors'

// Where a public form's submissions go — shared by the form, contact form and
// newsletter blocks. Every choice here is SAVED ON THE PAGE and read by the
// server (services/form_record.py); the browser only ever sends the block id,
// so none of it can be steered by a visitor.

interface DestinationFields {
  destination?: FormDestination
  submitAction?: string
  notifyEmail?: string
  record?: FormRecordTarget
}

interface Props {
  project: string
  transactions: { obj_name: string; title: string }[]
  block: DestinationFields
  /** The form's field names, in order (a newsletter has just `email`). */
  fieldNames: string[]
  onChange: (patch: Partial<DestinationFields>) => void
}

interface ColumnInfo { name: string; db_name: string; heading: string }

function headerColumns(model: unknown): ColumnInfo[] {
  try {
    const m = model as { transaction?: { sql_models?: { sql_model?: { columns?: unknown[] } }[] } }
    const cols = m.transaction?.sql_models?.[0]?.sql_model?.columns || []
    return cols.map((c) => {
      const col = ((c as { column?: unknown }).column ?? c) as Record<string, unknown>
      return {
        name: String(col.name || ''),
        db_name: String(col.db_name || col.name || ''),
        heading: String(col.heading || col.name || ''),
      }
    }).filter((c) => c.db_name)
  } catch {
    return []
  }
}

export function FormDestinationEditor({ project, transactions, block, fieldNames, onChange }: Props) {
  const destination: FormDestination = block.destination
    ?? (block.submitAction ? 'action' : 'submissions')
  const record = block.record
  const actions = usePageRuntime().getActions()
  const [columns, setColumns] = useState<ColumnInfo[]>([])
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let alive = true
    setColumns([])
    setLoadError('')
    setLoading(false)
    if (destination !== 'transaction' || !record?.obj_name || !project) return
    setLoading(true)
    loadTransactionObject(project, record.obj_name)
      .then((res) => { if (alive) setColumns(headerColumns(res?.content)) })
      .catch((e) => {
        if (alive) setLoadError(e instanceof Error ? e.message : 'Could not load the transaction')
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [destination, record?.obj_name, project])

  const setRecord = (patch: Partial<FormRecordTarget>) =>
    onChange({ record: { obj_name: record?.obj_name || '', ...record, ...patch } })
  // With no column list (the model failed to load, or has no header columns)
  // the mapping is still shown — as free text — so a saved mapping is never
  // hidden and can still be corrected.
  const haveColumns = columns.length > 0
  const objKnown = !record?.obj_name || transactions.some((t) => t.obj_name === record.obj_name)
  const actionKnown = !block.submitAction || actions.some((a) => a.name === block.submitAction)

  // A field with no explicit mapping saves to the same-named column.
  const autoTarget = useMemo(() => {
    const byKey = new Map<string, string>()
    for (const c of columns) {
      byKey.set(c.name.toLowerCase(), c.db_name)
      byKey.set(c.db_name.toLowerCase(), c.db_name)
    }
    return (field: string) => byKey.get(field.toLowerCase()) || ''
  }, [columns])

  const fixed = Object.entries(record?.set || {})

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <Field label="Where submissions go">
        <select
          value={destination}
          onChange={(e) => onChange({ destination: e.target.value as FormDestination })}
          style={inputStyle}
        >
          <option value="submissions">Form submissions inbox</option>
          <option value="email">Inbox + email me</option>
          <option value="transaction">Inbox + save as a record</option>
          <option value="action">Only my page action</option>
        </select>
      </Field>

      {/* The runtime runs a set action on EVERY destination (before the
          built-in one), so the picker is always offered — it is only
          mandatory for 'action'. */}
      <Field label={destination === 'action' ? 'Page action to run' : 'Also run a page action (optional)'}>
        <select
          value={block.submitAction || ''}
          onChange={(e) => onChange({ submitAction: e.target.value || undefined })}
          style={inputStyle}
        >
          <option value="">— none —</option>
          {!actionKnown && <option value={block.submitAction}>{block.submitAction} (not defined)</option>}
          {actions.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
        </select>
      </Field>
      {destination === 'action' && !block.submitAction && (
        <small style={{ color: 'var(--color-warning, #d97706)', fontSize: 13 }}>
          With no action chosen this form submits nowhere.
          {actions.length === 0 && ' Add one in Page properties → Actions.'}
        </small>
      )}

      {(destination === 'email' || destination === 'transaction') && (
        <Field label={destination === 'email' ? 'Email each submission to' : 'Also email to (optional)'}>
          <input
            type="email" value={block.notifyEmail || ''} placeholder="sales@example.com"
            onChange={(e) => onChange({ notifyEmail: e.target.value || undefined })}
            style={inputStyle}
          />
        </Field>
      )}

      {destination === 'transaction' && (
        <>
          <Field label="Save into">
            <select
              value={record?.obj_name || ''}
              // Keep the field mappings, fixed values and record column: most
              // carry over between similar objects, and a stale one is shown
              // below (flagged) for the author to fix rather than lost.
              onChange={(e) => onChange({ record: { ...(record || {}), obj_name: e.target.value } })}
              style={inputStyle}
            >
              <option value="">— choose a transaction —</option>
              {!objKnown && (
                // While the list is still loading the saved name is shown plain.
                <option value={record?.obj_name}>{record?.obj_name}{transactions.length ? ' (not found)' : ''}</option>
              )}
              {transactions.map((t) => (
                <option key={t.obj_name} value={t.obj_name}>{t.title || t.obj_name}</option>
              ))}
            </select>
          </Field>
          {loadError && <small style={{ color: 'var(--color-error)', fontSize: 13 }}>{loadError}</small>}

          {record?.obj_name && !loading && (
            <>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                Each field saves to a column. Only spam-free submissions become records; every
                submission still lands in the inbox.
                {!haveColumns && ' The column list is unavailable — type column names (blank = the same-named column, "-" = don\'t save).'}
              </span>
              <div style={{ display: 'grid', gap: 6 }}>
                {fieldNames.map((f) => {
                  const mapped = record.fields?.[f]
                  const value = mapped ?? autoTarget(f)
                  const setField = (col: string) => {
                    const fields = { ...(record.fields || {}) }
                    if (col) fields[f] = col
                    else delete fields[f]
                    setRecord({ fields: Object.keys(fields).length ? fields : undefined })
                  }
                  if (!haveColumns) {
                    return (
                      <div key={f} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, alignItems: 'center' }}>
                        <code style={{ fontSize: 13 }}>{f}</code>
                        <input
                          type="text" value={mapped ?? ''} placeholder={f}
                          onChange={(e) => setField(e.target.value.trim())}
                          style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
                        />
                      </div>
                    )
                  }
                  const savedUnknown = !!value && value !== '-' && !columns.some((c) => c.db_name === value)
                  return (
                    <div key={f} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, alignItems: 'center' }}>
                      <code style={{ fontSize: 13 }}>{f}</code>
                      <select
                        value={value || '-'}
                        onChange={(e) => {
                          const fields = { ...(record.fields || {}) }
                          if (e.target.value === autoTarget(f) && e.target.value) delete fields[f]
                          else fields[f] = e.target.value
                          setRecord({ fields })
                        }}
                        style={inputStyle}
                      >
                        <option value="-">— don&apos;t save —</option>
                        {savedUnknown && <option value={value}>{value} (not in this transaction)</option>}
                        {columns.map((c) => (
                          <option key={c.db_name} value={c.db_name}>{c.heading} ({c.db_name})</option>
                        ))}
                      </select>
                    </div>
                  )
                })}
              </div>

              <Field label="Fixed values (column = value, one per line)">
                <DraftTextField
                  multiline rows={2}
                  value={fixed.map(([k, v]) => `${k} = ${v}`).join('\n')}
                  placeholder="STATUS = NEW"
                  onCommit={(text) => {
                    const set: Record<string, string> = {}
                    for (const line of text.split('\n')) {
                      const i = line.indexOf('=')
                      if (i > 0) set[line.slice(0, i).trim()] = line.slice(i + 1).trim()
                    }
                    setRecord({ set: Object.keys(set).length ? set : undefined })
                  }}
                  style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
                />
              </Field>

              <Field label="Column for the page's record (item pages only)">
                {haveColumns ? (
                  <select
                    value={record.recordColumn || ''}
                    onChange={(e) => setRecord({ recordColumn: e.target.value || undefined })}
                    style={inputStyle}
                  >
                    <option value="">— none —</option>
                    {record.recordColumn && !columns.some((c) => c.db_name === record.recordColumn) && (
                      <option value={record.recordColumn}>{record.recordColumn} (not in this transaction)</option>
                    )}
                    {columns.map((c) => (
                      <option key={c.db_name} value={c.db_name}>{c.heading} ({c.db_name})</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text" value={record.recordColumn || ''} placeholder="— none —"
                    onChange={(e) => setRecord({ recordColumn: e.target.value.trim() || undefined })}
                    style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
                  />
                )}
              </Field>
            </>
          )}
        </>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
}
